// client/src/services/api.js
import axios from 'axios';

const API = axios.create({
  baseURL: 'https://api.soccerup.games/api',
  withCredentials: true,
  timeout: 30000,
});

// إضافة اعتراض لإضافة timestamp لمنع الكاش وتسريع الاستجابات
API.interceptors.request.use((config) => {
  // إضافة timestamp للطلبات GET لمنع الكاش
  if (config.method === 'get') {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// Response interceptor to handle authentication errors globally
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear local storage and redirect to login
      localStorage.removeItem('userId');
      localStorage.removeItem('username');
      localStorage.removeItem('continent');
      if (!window.location.pathname.includes('/')) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// ========== AUTHENTICATION ==========
export const register = (username, password, continent, email) => 
  API.post('/user/register', { username, password, continent, email });

export const login = (username, password) => API.post('/user/login', { username, password });

// ========== PASSWORD RESET ==========
export const forgotPassword = (email) => API.post('/user/forgot-password', { email });
export const resetPassword = (token, newPassword) => API.post('/user/reset-password', { token, newPassword });

// ========== CARDS & USER CARDS ==========
export const fetchAllCards = () => API.get('/cards/all');
export const fetchStarterCards = () => API.get('/cards/starter');
export const fetchFixedCards = () => API.get('/cards/fixed');
export const fetchCardsByRarity = (rarity) => API.get(`/cards/by-rarity/${rarity}`);
export const fetchUserCards = (userId) => API.get(`/cards/user-cards/${userId}`);
export const fetchUserTeam = (userId) => API.get(`/cards/user-team/${userId}`);
export const setUserTeam = (userId, selectedCardIds) =>
  API.post(`/cards/set-team/${userId}`, { selectedCardIds });

// ========== SQUAD MANAGEMENT ==========
export const fetchUserSquad = (userId) => API.get(`/squad/${userId}`);
export const saveUserSquad = (userId, activeCardIds, benchCardIds) =>
  API.post(`/squad/${userId}`, { activeCardIds, benchCardIds });

// ========== CAPTAIN MANAGEMENT ==========
export const setCaptain = (userCardId) =>
  API.post('/squad/set-captain', { user_card_id: userCardId });

// ========== MATCHES ==========
export const playMatch = (userId, opponentId, formationUser, formationOpp, subsFirstHalf = [], subsSecondHalf = []) =>
  API.post('/matches/play', { userId, opponentId, formationUser, formationOpp, subsFirstHalf, subsSecondHalf });
export const forfeitMatch = (userId, opponentId, matchId) =>
  API.post('/matches/forfeit', { userId, opponentId, matchId });

// ========== LEAGUE & STANDINGS ==========
export const fetchStandings = (competition, continent = null) => {
  let url = `/league/standings/${competition}`;
  if (competition === 'domestic' && continent) {
    url += `?continent=${continent}`;
  }
  return API.get(url);
};

// fetch qualified users for continental competitions
export const fetchQualifiedUsers = (competition) => 
  API.get(`/league/qualification/${competition}`);

// ========== USER PROFILE & DAILY STATS ==========
export const getProfile = (userId) => API.get(`/user/profile/${userId}`);

export const getUserDailyStats = async (userId) => {
  const res = await getProfile(userId);
  return {
    domestic: 5 - (res.data.domestic_matches_today || 0),
    champions: 5 - (res.data.champions_matches_today || 0),
    europa: 5 - (res.data.europa_matches_today || 0)
  };
};

export const getRemainingMatches = async (userId) => {
  const stats = await getUserDailyStats(userId);
  return stats.domestic;
};

// ========== ADMIN ENDPOINTS ==========
export const resetDomesticSeason = (continent) =>
  API.post(`/admin/reset-domestic-season/${continent}`);
export const resetContinentalSeason = (competition) =>
  API.post(`/admin/reset-continental-season/${competition}`);
export const getQualifiedUsers = (competition, continent = null) => {
  let url = `/admin/qualified-users/${competition}`;
  if (competition === 'domestic' && continent) url += `?continent=${continent}`;
  return API.get(url);
};

// ========== SUPPORT & NOTIFICATIONS ==========
export const fetchUserMessages = () => API.get('/support/user-messages');
export const fetchNotifications = () => API.get('/notifications');
export const markNotificationRead = (notificationUserId) => 
  API.put(`/notifications/${notificationUserId}/read`);
export const markAllNotificationsRead = () => 
  API.put('/notifications/read-all');
export const deleteNotification = (notificationUserId) => 
  API.delete(`/notifications/${notificationUserId}`);

// ========== TEAM NAME MANAGEMENT ==========
export const updateTeamName = (teamName) => API.put('/user/team-name', { teamName });

// ========== LEGACY & UTILITY ==========
export const resetSeason = () => API.post('/league/reset-season');
export const fetchOldStandings = () => API.get('/league/standings');
export const fetchSelectionData = () => API.get('/cards/selection-data');
export const fetchCurrentSeason = (competition, continent = null) => {
  let url = `/league/current-season/${competition}`;
  if (competition === 'domestic' && continent) {
    url += `?continent=${continent}`;
  }
  return API.get(url);
};
export const verifyEmail = (userId, code) => API.post('/user/verify-email', { userId, code });
// ========== PREMIUM TEAM NAMES ==========
export const fetchPremiumNames = () => API.get('/premium-names');
export const purchasePremiumName = (nameId) => API.post('/premium-names/purchase', { nameId });
export const adminFetchPremiumNames = () => API.get('/premium-names/admin');
export const adminAddPremiumName = (name, tier, price) =>
  API.post('/premium-names/admin/add', { name, tier, price });
// ===== Landing Page Stats =====
export const fetchLandingStats = () => API.get('/statistics/landing');

export const updateGhostSettings = (allow) => API.put('/user/ghost-settings', { allow });

// ========== USER SETTINGS (Formation & Playing Style) ==========
export const updateUserSettings = (formation, playingStyle) => 
  API.put('/user/settings', { formation, playingStyle });

export const resendVerification = (userId) => API.post('/user/resend-verification', { userId });


export default API;